import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';

import { AgentEntry } from '@Models';
import { getStorageItem, setStorageItem } from '../Helpers/Agents/storage';
import { screenSize } from '../Themes/sizes';

export type AgentMode = 'sidebar' | 'floating' | 'fullscreen';

export const AGENT_STORAGE_KEYS = {
  MODE: 'bops.agent.mode',
  NAV_COLLAPSED: 'bops.agent.navCollapsed',
} as const;

export interface AgentContextState {
  isOpen: boolean;
  mode: AgentMode;
  isNavSidebarCollapsed: boolean;
  isPreferencesOpen: boolean;
  activeChatId: string | null;
  selectedAgent: AgentEntry | undefined;
  initialMessage: string | null;
  pendingJourneyId: string | null;
}

export type AgentAction =
  | { type: 'SET_IS_OPEN'; value: boolean }
  | { type: 'SET_MODE'; value: AgentMode }
  | { type: 'SET_ACTIVE_CHAT_ID'; value: string | null }
  | { type: 'SET_SELECTED_AGENT'; value: AgentEntry | undefined }
  | { type: 'SET_INITIAL_MESSAGE'; value: string | null }
  | { type: 'SET_PENDING_JOURNEY_ID'; value: string | null }
  | { type: 'SET_NAV_SIDEBAR_COLLAPSED'; value: boolean }
  | { type: 'SET_PREFERENCES_OPEN'; value: boolean };

export interface AgentContextValue extends AgentContextState {
  setIsOpen: (value: boolean) => void;
  setMode: (value: AgentMode) => void;
  setActiveChatId: (value: string | null) => void;
  setSelectedAgent: (value: AgentEntry | undefined) => void;
  selectAgent: (value: AgentEntry | undefined) => void;
  setInitialMessage: (value: string | null) => void;
  setPendingJourneyId: (value: string | null) => void;
  setNavSidebarCollapsed: (value: boolean) => void;
  setPreferencesOpen: (value: boolean) => void;
  toggleNavSidebar: () => void;
  openPreferences: () => void;
  closePreferences: () => void;
}

const VALID_MODES: AgentMode[] = ['sidebar', 'floating', 'fullscreen'];

const isValidMode = (value: string | null): value is AgentMode =>
  value !== null && VALID_MODES.includes(value as AgentMode);

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isAgentRoute = (): boolean => {
  if (typeof window === 'undefined') return false;
  const p = window.location.pathname;
  return p === '/agent' || p.startsWith('/agent/');
};

const detectInitialMode = (): AgentMode => {
  if (typeof window === 'undefined') return 'sidebar';
  if (isAgentRoute()) return 'fullscreen';
  if (window.innerWidth < screenSize.tablet) return 'fullscreen';
  const persisted = getStorageItem(AGENT_STORAGE_KEYS.MODE);
  return isValidMode(persisted) ? persisted : 'sidebar';
};

const detectInitialOpen = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (isAgentRoute()) return true;
  return window.innerWidth >= screenSize.mobile;
};

const detectInitialActiveChatId = (): string | null => {
  if (typeof window === 'undefined') return null;
  const match = window.location.pathname.match(/^\/agent\/([^/]+)$/);
  if (!match) return null;
  return UUID_RE.test(match[1]) ? match[1] : null;
};

const initialState: AgentContextState = {
  isOpen: detectInitialOpen(),
  mode: detectInitialMode(),
  isNavSidebarCollapsed: getStorageItem(AGENT_STORAGE_KEYS.NAV_COLLAPSED) === 'true',
  isPreferencesOpen: false,
  activeChatId: detectInitialActiveChatId(),
  selectedAgent: undefined,
  initialMessage: null,
  pendingJourneyId: null,
};

const reducer = (state: AgentContextState, action: AgentAction): AgentContextState => {
  switch (action.type) {
    case 'SET_IS_OPEN':
      return state.isOpen === action.value ? state : { ...state, isOpen: action.value };
    case 'SET_MODE':
      return state.mode === action.value ? state : { ...state, mode: action.value };
    case 'SET_ACTIVE_CHAT_ID':
      return state.activeChatId === action.value ? state : { ...state, activeChatId: action.value };
    case 'SET_SELECTED_AGENT': {
      if (state.selectedAgent?.source_name === action.value?.source_name) return state;
      return { ...state, selectedAgent: action.value };
    }
    case 'SET_INITIAL_MESSAGE':
      return state.initialMessage === action.value ? state : { ...state, initialMessage: action.value };
    case 'SET_PENDING_JOURNEY_ID':
      return state.pendingJourneyId === action.value ? state : { ...state, pendingJourneyId: action.value };
    case 'SET_NAV_SIDEBAR_COLLAPSED':
      return state.isNavSidebarCollapsed === action.value ? state : { ...state, isNavSidebarCollapsed: action.value };
    case 'SET_PREFERENCES_OPEN':
      return state.isPreferencesOpen === action.value ? state : { ...state, isPreferencesOpen: action.value };
    default:
      return state;
  }
};

const AgentContext = createContext<AgentContextValue | undefined>(undefined);

interface AgentProviderProps {
  children: ReactNode;
}

export const AgentProvider = ({ children }: AgentProviderProps) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    setStorageItem(AGENT_STORAGE_KEYS.MODE, state.mode);
  }, [state.mode]);

  useEffect(() => {
    setStorageItem(AGENT_STORAGE_KEYS.NAV_COLLAPSED, String(state.isNavSidebarCollapsed));
  }, [state.isNavSidebarCollapsed]);

  // Below the tablet breakpoint the agent must be fullscreen: the sidebar/floating
  // panels have fixed widths that overflow narrow viewports. Enforce on mount (a phone
  // loads small and never fires a resize event to self-correct) and on every resize.
  // The nav sidebar is collapsed so it doesn't eat the whole screen on a phone.
  // The reducer no-ops when values already match, so manual toggles between events stick.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const enforceMobileLayout = () => {
      if (window.innerWidth < screenSize.tablet) {
        dispatch({ type: 'SET_MODE', value: 'fullscreen' });
        dispatch({ type: 'SET_NAV_SIDEBAR_COLLAPSED', value: true });
      }
    };
    enforceMobileLayout();
    window.addEventListener('resize', enforceMobileLayout);
    return () => window.removeEventListener('resize', enforceMobileLayout);
  }, []);

  const setIsOpen = useCallback((value: boolean) => dispatch({ type: 'SET_IS_OPEN', value }), []);
  const setMode = useCallback((value: AgentMode) => dispatch({ type: 'SET_MODE', value }), []);
  const setActiveChatId = useCallback((value: string | null) => dispatch({ type: 'SET_ACTIVE_CHAT_ID', value }), []);
  const setSelectedAgent = useCallback(
    (value: AgentEntry | undefined) => dispatch({ type: 'SET_SELECTED_AGENT', value }),
    [],
  );
  // Manual agent switch — resets the active chat. Matches legacy `selectAgent`
  // semantics so callers (e.g. ConnectorPicker) can express intent explicitly.
  const selectAgent = useCallback(
    (value: AgentEntry | undefined) => {
      if (state.selectedAgent?.source_name === value?.source_name) return;
      if (state.selectedAgent !== undefined) {
        dispatch({ type: 'SET_ACTIVE_CHAT_ID', value: null });
      }
      dispatch({ type: 'SET_SELECTED_AGENT', value });
    },
    [state.selectedAgent],
  );
  const setInitialMessage = useCallback((value: string | null) => dispatch({ type: 'SET_INITIAL_MESSAGE', value }), []);
  const setPendingJourneyId = useCallback(
    (value: string | null) => dispatch({ type: 'SET_PENDING_JOURNEY_ID', value }),
    [],
  );
  const setNavSidebarCollapsed = useCallback(
    (value: boolean) => dispatch({ type: 'SET_NAV_SIDEBAR_COLLAPSED', value }),
    [],
  );
  const setPreferencesOpen = useCallback((value: boolean) => dispatch({ type: 'SET_PREFERENCES_OPEN', value }), []);
  const toggleNavSidebar = useCallback(
    () => dispatch({ type: 'SET_NAV_SIDEBAR_COLLAPSED', value: !state.isNavSidebarCollapsed }),
    [state.isNavSidebarCollapsed],
  );
  const openPreferences = useCallback(() => setPreferencesOpen(true), [setPreferencesOpen]);
  const closePreferences = useCallback(() => setPreferencesOpen(false), [setPreferencesOpen]);

  const value = useMemo<AgentContextValue>(
    () => ({
      ...state,
      setIsOpen,
      setMode,
      setActiveChatId,
      setSelectedAgent,
      selectAgent,
      setInitialMessage,
      setPendingJourneyId,
      setNavSidebarCollapsed,
      setPreferencesOpen,
      toggleNavSidebar,
      openPreferences,
      closePreferences,
    }),
    [
      state,
      setIsOpen,
      setMode,
      setActiveChatId,
      setSelectedAgent,
      selectAgent,
      setInitialMessage,
      setPendingJourneyId,
      setNavSidebarCollapsed,
      setPreferencesOpen,
      toggleNavSidebar,
      openPreferences,
      closePreferences,
    ],
  );

  return <AgentContext.Provider value={value}>{children}</AgentContext.Provider>;
};

export const useAgentContext = (): AgentContextValue => {
  const ctx = useContext(AgentContext);
  if (ctx === undefined) {
    throw new Error('useAgentContext must be used within <AgentProvider>');
  }
  return ctx;
};
